import {
  BaseText,
  SpacedColumnHeader,
  YearContainer,
  YearText
} from '@features/reports/components/riskAssessment/CommonStyledComponents'
import RiskAssessmentCell from '@features/reports/components/riskAssessment/RiskAssessmentCell/RiskAssessmentCell'
import { generateYearMonthsArray } from '@features/reports/utils/riskAssessmentUtils'
import type { PerResidentRow } from '@lmc/ts-common'
import { Table, type TableColumnGroupType } from 'antd'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { riskAssessmentReportConstants as translations } from 'locale/riskAssessmentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

interface DashboardTableProps {
  dataSource: PerResidentRow[]
  to: dayjs.Dayjs
  from: dayjs.Dayjs
}

const DashboardTable = ({ dataSource, to, from }: DashboardTableProps) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENT_REPORT)
  const yearMonths = generateYearMonthsArray(from, to)

  const columns: TableColumnGroupType<PerResidentRow>[] = [
    {
      title: ' ',
      children: [
        {
          dataIndex: 'form',
          title: (
            <SpacedColumnHeader>
              <BaseText weight="medium">
                {t(translations.RISK_ASSESSMENT)}
              </BaseText>
            </SpacedColumnHeader>
          ),
          key: 'form',
          fixed: 'left',
          sorter: (a: PerResidentRow, b: PerResidentRow) =>
            a.form.name?.localeCompare(b.form?.name),
          defaultSortOrder: 'ascend',
          render: (row) => (
            <AssessmentCard>
              <BaseText weight="bold">{row?.name}</BaseText>
            </AssessmentCard>
          )
        }
      ]
    },
    ...yearMonths.map((yearMonth) => ({
      title: (
        <YearContainer>
          <YearText level={4} weight="regular">
            {yearMonth.year}
          </YearText>
        </YearContainer>
      ),
      children: yearMonth.months.map((month) => ({
        dataIndex: 'months',
        title: (
          <BaseText weight="medium">
            {dayjs(month, 'MM/YYYY').format('MMM')}
          </BaseText>
        ),
        key: `assessments-${month}`,
        render: (row: PerResidentRow['months']) => (
          <RiskAssessmentCell row={row} month={month} />
        )
      }))
    }))
  ]

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      scroll={{ x: 'max-content' }}
      bordered
      pagination={{
        showSizeChanger: true,
        position: ['bottomCenter']
      }}
    />
  )
}

const AssessmentCard = styled.div`
  width: 220px;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    width: 100px;
  }
`

export default DashboardTable
